/**
 * Rebindable controls. Every action can have up to two bindings; a binding
 * is a key (KeyboardEvent.code, so it follows physical position) or a mouse
 * button, optionally with Shift held:
 *   'KeyQ'  'Space'  'Mouse0' (left)  'Mouse1' (middle)  'Mouse2' (right)
 *   'Shift+Mouse0'  'Shift+KeyV'
 * Lookup tries the exact binding first, then the same input without Shift,
 * so holding Shift for an area cast never blocks queueing elements.
 *
 * Pure data and functions (no DOM): Controls and the settings screen use it,
 * and it is unit tested in node.
 */

import type { BaseElement } from '@shared/index.js';

export type BindAction =
  | BaseElement
  | 'force' | 'area' | 'self' | 'weapon' | 'magick' | 'clear' | 'weave' | 'stash1' | 'stash2'
  | 'walk' | 'moveUp' | 'moveDown' | 'moveLeft' | 'moveRight'
  | 'spellbook' | 'chat' | 'mute' | 'settings' | 'pause' | 'ready';

export type Binding = string;
export type Keymap = Record<BindAction, [Binding | null, Binding | null]>;

export const DEFAULT_KEYMAP: Readonly<Keymap> = {
  water: ['KeyQ', null], life: ['KeyW', null], shield: ['KeyE', null], cold: ['KeyR', null],
  lightning: ['KeyA', null], arcane: ['KeyS', null], earth: ['KeyD', null], fire: ['KeyF', null],
  force: ['Mouse0', null],
  area: ['Shift+Mouse0', null],
  self: ['Mouse1', null],
  // Mirrors Magicka (whose mouse buttons are swapped relative to ours): Shift + the walk button
  // imbues the staff when elements are queued, and swings it when the queue is empty.
  weapon: ['Shift+Mouse2', 'KeyV'],
  magick: ['Space', 'KeyG'],
  // Escape cleared the queue until 2026-10-28; it opens the menu (and pauses a solo game) now.
  clear: ['Backspace', null],
  // Deeper spells (docs/spells-depth.md): C weaves the last two pieces, Z and X tap the two stash slots.
  weave: ['KeyC', null],
  stash1: ['KeyZ', null],
  stash2: ['KeyX', null],
  walk: ['Mouse2', null],
  moveUp: ['ArrowUp', null], moveDown: ['ArrowDown', null], moveLeft: ['ArrowLeft', null], moveRight: ['ArrowRight', null],
  spellbook: ['Tab', null],
  chat: ['Enter', 'NumpadEnter'],
  mute: ['KeyM', null],
  settings: ['KeyO', null],
  pause: ['Escape', 'KeyP'],
  // Ready for what comes next: starts the next wave now, skips the countdown or the results (everyone's press, alone one)
  ready: ['KeyN', null],
};

export const ELEMENT_ACTIONS: readonly BaseElement[] = ['water', 'life', 'shield', 'cold', 'lightning', 'arcane', 'earth', 'fire'];

export const ACTION_LABELS: Readonly<Record<BindAction, string>> = {
  water: 'Water', life: 'Life', shield: 'Shield', cold: 'Cold', lightning: 'Lightning', arcane: 'Arcane', earth: 'Earth', fire: 'Fire',
  force: 'Cast forward (hold for beams, sprays, charge)',
  area: 'Cast around you (area)',
  self: 'Cast on yourself',
  weapon: 'Staff: imbue if queued, otherwise swing',
  magick: 'Cast Magick',
  clear: 'Clear queue',
  weave: 'Weave the last two pieces',
  stash1: 'Stash slot 1 (put away / pull back)',
  stash2: 'Stash slot 2 (put away / pull back)',
  walk: 'Walk toward cursor (hold)',
  moveUp: 'Walk up', moveDown: 'Walk down', moveLeft: 'Walk left', moveRight: 'Walk right',
  spellbook: 'Spellbook (hold)',
  chat: 'Chat',
  mute: 'Mute sound',
  settings: 'Settings',
  pause: 'Menu (pauses when you play alone)',
  ready: 'Ready: skip the wait (next wave, countdown, results)',
};

export const ACTION_GROUPS: readonly { title: string; actions: readonly BindAction[] }[] = [
  { title: 'Elements', actions: ELEMENT_ACTIONS },
  { title: 'Casting', actions: ['force', 'area', 'self', 'weapon', 'magick', 'clear'] },
  { title: 'Weaving', actions: ['weave', 'stash1', 'stash2'] },
  { title: 'Movement', actions: ['walk', 'moveUp', 'moveDown', 'moveLeft', 'moveRight'] },
  { title: 'Interface', actions: ['spellbook', 'chat', 'mute', 'settings', 'pause', 'ready'] },
];

/** Actions that do something on release as well as press. */
export const HOLD_ACTIONS: ReadonlySet<BindAction> = new Set<BindAction>(['force', 'walk', 'spellbook', 'moveUp', 'moveDown', 'moveLeft', 'moveRight']);

const MODIFIER_CODES = new Set(['ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight', 'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight']);

/** A modifier on its own is never a binding (Shift is a prefix, Ctrl/Alt/Meta belong to the browser). */
export function isModifierCode(code: string): boolean {
  return MODIFIER_CODES.has(code);
}

export type SettingsKeyAction = 'close' | 'cancel' | 'wait' | 'bind' | 'pass';

/**
 * What a key press does while the settings panel is open. Escape cancels a
 * capture, and with no capture running it closes the panel like any dialog:
 * the game ignores keys while settings are open, so before this only the Done
 * button got you out.
 */
export function settingsKeyAction(capturing: boolean, code: string, shift: boolean): SettingsKeyAction {
  if (!capturing) return code === 'Escape' ? 'close' : 'pass';
  if (code === 'Escape' && !shift) return 'cancel';
  if (isModifierCode(code)) return 'wait';
  return 'bind';
}

export function keyBinding(code: string, shift: boolean): Binding {
  return shift ? `Shift+${code}` : code;
}

export function mouseBinding(button: number, shift: boolean): Binding {
  return shift ? `Shift+Mouse${button}` : `Mouse${button}`;
}

/** Reverse index: binding -> action. Rebuild after every change. */
export function indexKeymap(map: Readonly<Keymap>): Map<Binding, BindAction> {
  const idx = new Map<Binding, BindAction>();
  for (const action of Object.keys(map) as BindAction[]) {
    for (const b of map[action]) if (b) idx.set(b, action);
  }
  return idx;
}

/** Exact binding first, then the same input without Shift. */
export function lookup(idx: ReadonlyMap<Binding, BindAction>, binding: Binding): BindAction | null {
  const exact = idx.get(binding);
  if (exact) return exact;
  if (binding.startsWith('Shift+')) return idx.get(binding.slice(6)) ?? null;
  return null;
}

/**
 * Assign `binding` to one slot of `action`. A binding can only do one thing,
 * so it is removed from whatever action held it before. Returns a new map.
 */
export function setBinding(map: Readonly<Keymap>, action: BindAction, slot: 0 | 1, binding: Binding | null): Keymap {
  const next = cloneKeymap(map);
  if (binding) {
    for (const a of Object.keys(next) as BindAction[]) {
      next[a] = next[a].map((b) => (b === binding ? null : b)) as [Binding | null, Binding | null];
    }
  }
  next[action][slot] = binding;
  return next;
}

export function cloneKeymap(map: Readonly<Keymap>): Keymap {
  const out = {} as Keymap;
  for (const a of Object.keys(map) as BindAction[]) out[a] = [map[a][0], map[a][1]];
  return out;
}

const MOUSE_NAMES: Readonly<Record<string, string>> = { Mouse0: 'Left click', Mouse1: 'Middle click', Mouse2: 'Right click', Mouse3: 'Mouse 4', Mouse4: 'Mouse 5' };

/** Human label: 'KeyQ' -> 'Q', 'Shift+Mouse0' -> 'Shift + Left click'. */
export function bindingLabel(b: Binding | null): string {
  if (!b) return '';
  const shift = b.startsWith('Shift+');
  const base = shift ? b.slice(6) : b;
  let name: string;
  if (MOUSE_NAMES[base]) name = MOUSE_NAMES[base]!;
  else if (base.startsWith('Mouse')) name = `Mouse ${Number(base.slice(5)) + 1}`;
  else if (base.startsWith('Key')) name = base.slice(3);
  else if (base.startsWith('Digit')) name = base.slice(5);
  else if (base.startsWith('Numpad')) name = `Num ${base.slice(6)}`;
  else if (base.startsWith('Arrow')) name = `${base.slice(5)} arrow`;
  else name = base;
  return shift ? `Shift + ${name}` : name;
}

/** Short cap for the HUD element tiles: the first binding, abbreviated. */
export function shortLabel(b: Binding | null): string {
  if (!b) return '';
  const label = bindingLabel(b);
  return label.replace('Shift + ', 'Sh+').replace(' click', '').replace(' arrow', '');
}

const STORAGE_KEY = 'spellweave.keymap';

export interface KeymapStorage { getItem(k: string): string | null; setItem(k: string, v: string): void }

/**
 * Default keys an action gave up: a map saved back then still holds them on the old action, and they move to the
 * new one the first time that map loads (2026-10-28: Escape went from Clear queue to the menu).
 */
const RETIRED_DEFAULTS: readonly { from: BindAction; binding: Binding; to: BindAction }[] = [
  { from: 'clear', binding: 'Escape', to: 'pause' },
];

/**
 * Saved map merged over the defaults, so actions added later get their default binding: each default key that no
 * saved action holds (a player's own binding wins), plus a retired default moving to its new action.
 */
export function loadKeymap(storage: KeymapStorage | null): Keymap {
  let out = cloneKeymap(DEFAULT_KEYMAP);
  if (!storage) return out;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return out;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return out;
    const added: BindAction[] = [];
    for (const a of Object.keys(out) as BindAction[]) {
      const v = (parsed as Record<string, unknown>)[a];
      if (Array.isArray(v) && v.length === 2 && v.every((b) => b === null || typeof b === 'string')) {
        out[a] = [v[0] as Binding | null, v[1] as Binding | null];
      } else {
        added.push(a);
      }
    }
    const saved = new Set<Binding>();
    for (const a of Object.keys(out) as BindAction[]) if (!added.includes(a)) for (const b of out[a]) if (b) saved.add(b);
    for (const a of added) {
      out[a] = [null, null];
      DEFAULT_KEYMAP[a].forEach((b, slot) => {
        if (!b) return;
        const retired = RETIRED_DEFAULTS.some((r) => r.to === a && r.binding === b && out[r.from].includes(b));
        if (!saved.has(b) || retired) out = setBinding(out, a, slot as 0 | 1, b);
      });
    }
  } catch {
    return cloneKeymap(DEFAULT_KEYMAP);
  }
  return out;
}

export function saveKeymap(storage: KeymapStorage | null, map: Readonly<Keymap>): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* private mode or full storage: bindings still apply for this session */
  }
}
